/**
 * Risk Matrix Modal Component
 * 
 * Visual Risk Matrix for selecting Objective Risk Ranking
 * Used in Tab 2 - Objectives and Risks
 * 
 * Features:
 * - 5x5 matrix (Likelihood vs Impact Severity)
 * - Clickable cells that return risk level (High, Med, Low)
 * - Auto-close on selection
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import styled from 'styled-components';
import { RootState } from '../../../../store';
import RiskMatrixTable from '../tab4/RiskMatrixTable';

// ========== TYPES ==========

interface RiskMatrixModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (
    riskLevel: 'High' | 'Med' | 'Low',
    severityIndex: number,
    likelihoodIndex: number
  ) => void;
  selectedSeverityIndex?: number | null;
  selectedLikelihoodIndex?: number | null;
}

// ========== STYLED COMPONENTS ==========

const ModalContent = styled.div`
`;

const MatrixTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 18px;
  color: #1F2937;
  margin-bottom: 16px;
  text-align: center;
`;

const MatrixDescription = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #6B7280;
  margin-bottom: 24px;
  text-align: center;
`;

const RiskMatrixContainer = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

// ========== COMPONENT ==========

const RiskMatrixModal: React.FC<RiskMatrixModalProps> = ({
  open,
  onClose,
  onSelect,
  selectedSeverityIndex,
  selectedLikelihoodIndex,
}) => {
  // Get rubric items from Redux
  const likelihoodRubricItems = useSelector((state: RootState) => state.riskAssessment.likelihoodRubricItems);
  const severityRubricItems = useSelector((state: RootState) => state.riskAssessment.severityRubricItems);

  // Risk matrix configuration (5x5) - still hardcoded as it's not part of rubric items
  const riskMatrix: ('High' | 'Med' | 'Low')[][] = [
    ['Med', 'Med', 'High', 'High', 'High'],    // Catastrophic
    ['Low', 'Med', 'Med', 'High', 'High'],     // Major
    ['Low', 'Med', 'Med', 'High', 'High'],     // Moderate
    ['Low', 'Low', 'Med', 'Med', 'Med'],       // Minor
    ['Low', 'Low', 'Low', 'Low', 'Med'],       // Insignificant
  ];

  // Sort likelihood items by index descending (for column order)
  const sortedLikelihoodItems = [...likelihoodRubricItems].sort((a, b) => b.index - a.index);

  // Column headers: from left to right (use Redux data if available, otherwise fallback to hardcoded)
  const likelihoodLabels = sortedLikelihoodItems.length > 0
    ? sortedLikelihoodItems.map(item => item.name)
    : ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];

  // Likelihood descriptions (matching column order)
  const likelihoodDescriptions = sortedLikelihoodItems.length > 0
    ? sortedLikelihoodItems.map(item => item.description)
    : [];

  // Row headers: from top to bottom (use Redux data if available, otherwise fallback to hardcoded)
  const impactLabels = severityRubricItems.length > 0
    ? severityRubricItems.map(item => item.name)
    : ['Catastrophic', 'Major', 'Moderate', 'Minor', 'Insignificant'];

  // Impact descriptions (matching row order)
  const impactDescriptions = severityRubricItems.length > 0
    ? severityRubricItems.map(item => item.description)
    : [];

  const handleCellClick = (riskLevel: 'High' | 'Med' | 'Low', rowIndex: number, colIndex: number) => {
    onSelect(riskLevel, rowIndex, colIndex);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      styles={{
        body: {
          padding: '24px',
        },
      }}
    >
      <ModalContent>
        <MatrixTitle>Risk Matrix</MatrixTitle>
        <MatrixDescription>
          Select a cell to set the Objective Risk Ranking based on Likelihood and Impact Severity
        </MatrixDescription>

        <RiskMatrixContainer>
          <RiskMatrixTable
            likelihoodLabels={likelihoodLabels}
            likelihoodDescriptions={likelihoodDescriptions}
            impactLabels={impactLabels}
            impactDescriptions={impactDescriptions}
            riskMatrix={riskMatrix}
            interactive={true}
            onCellClick={handleCellClick}
            selectedRow={selectedSeverityIndex ?? undefined}
            selectedCol={selectedLikelihoodIndex ?? undefined}
            showTitle={true}
          /> 
          </RiskMatrixContainer>
      </ModalContent>
    </Modal>
  );
};

export default RiskMatrixModal;

